/** Optimise: how much less a client could pay for the same work.
 *
 *  The client's workloads as they run today (the Simulate document, each workload with a use case), then the
 *  savings ladder the server priced (tools.optimise): one row per lever, largest first, each a switch; the
 *  applied rows are composed and priced together as the optimised plan, beside today, month by month. Every
 *  figure is the server's; this page draws them and sends the switches back. */
import { useEffect, useState } from 'react'
import { api } from '../api'
import { useSettings } from '../settings'
import type { CatalogModel, OptimiseLever, OptimiseUseCase, Optimised, RegionChoices, SimulationDoc, SimulationWorkload } from '../types'
import { ExportPdf } from './ExportPdf'
import { RegionPicker, regionsFor, useRegions } from './RegionPicker'
import { WorkloadCard, fresh, type Model } from './Simulations'
import { TimeStep, usePeriod, type Period } from './TimeStep'
import { ErrorNote, Help, compact, usd } from './ui'
import { Useful } from './Useful'
import { useAsync, useDebounced } from './useAsync'

type Go = (v: 'simulations' | 'simulation' | 'calculator' | 'compare' | 'assessment') => void
interface Mine { id: string; edit?: string; name: string; client: string }
function readMine(): Mine[] {
  try { const v = JSON.parse(localStorage.getItem('simulations') ?? '[]'); return Array.isArray(v) ? v : [] } catch { return [] }
}
const LEVER_LABEL: Record<string, string> = {
  mode: 'hosting mode', batch: 'batch tier', flex: 'latency-tolerant tier', right_size: 'right-size the model', routing: 'routing',
  caching: 'prompt caching', context: 'context discipline', region: 'region', spot: 'spot',
}

/** Today against the optimised plan, month by month. The figures are the server's; this draws them. */
function Chart({ months, today, optimised }: { months: number; today: number[]; optimised: number[] }) {
  const W = 760, H = 200, L = 62, R = 14, T = 12, B = 30
  const max = Math.max(1, ...today, ...optimised)
  const pow = 10 ** Math.floor(Math.log10(max))
  const top = ([1, 2, 2.5, 5, 10].map((k) => k * pow).find((v) => v >= max) ?? 10 * pow)
  const x = (m: number) => L + (months > 1 ? (m * (W - L - R)) / (months - 1) : 0)
  const y = (c: number) => T + (1 - c / top) * (H - T - B)
  const every = months > 24 ? 6 : months > 12 ? 4 : 2
  const path = (v: number[]) => v.map((c, m) => `${m ? 'L' : 'M'}${x(m).toFixed(1)} ${y(c).toFixed(1)}`).join(' ')
  return (
    <div className="overtime simchart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Monthly cost today and optimised over ${months} months`}>
        {[0, 1, 2, 3, 4].map((k) => (
          <g key={k}><line x1={L} x2={W - R} y1={y((top * k) / 4)} y2={y((top * k) / 4)} className="grid" />
            <text x={L - 8} y={y((top * k) / 4) + 4} textAnchor="end">{usd((top * k) / 4, 0)}</text></g>
        ))}
        {Array.from({ length: months }, (_, m) => m).filter((m) => m === months - 1 || (m % every === 0 && months - 1 - m > every / 2)).map((m) => (
          <text key={m} x={x(m)} y={H - 8} textAnchor={m === months - 1 ? 'end' : m === 0 ? 'start' : 'middle'}>month {m + 1}</text>
        ))}
        <path d={path(today)} fill="none" stroke="var(--ink)" strokeWidth={2.5} />
        <path d={path(optimised)} fill="none" stroke="#15803d" strokeWidth={2.5} />
      </svg>
      <div className="legend muted small">
        <span><i style={{ background: 'var(--ink)' }} /> Today, {usd(today[0], 0)} → {usd(today[today.length - 1], 0)} a month</span>
        <span><i style={{ background: '#15803d' }} /> Optimised, {usd(optimised[0], 0)} → {usd(optimised[optimised.length - 1], 0)} a month</span>
      </div>
    </div>
  )
}

function Switch({ on, disabled, onChange, label }: { on: boolean; disabled?: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} className={`switch${on ? ' on' : ''}`} disabled={disabled}
      onClick={() => onChange(!on)}><span className="knob" /></button>
  )
}

export function OptimisePage({ embedded = false, doc: given }: { go?: Go; embedded?: boolean; doc?: SimulationDoc }) {
  const settings = useSettings()
  const vendors = settings.vendors
  const [regions, setRegions] = useRegions()
  const [period, setPeriod] = usePeriod()
  const [ownDoc, setDoc] = useState<SimulationDoc>(() => {
    const d = fresh(regions, period)
    return { ...d, months: 12, workloads: d.workloads.map((w) => ({ ...w, label: 'Workload 1', use_case: 'chat' })) }
  })
  // the lever ids applied: null until the first answer, then each lever's default, then whatever is switched
  const doc = given ?? ownDoc
  const [applied, setApplied] = useState<string[] | null>(null)
  const [mine] = useState<Mine[]>(readMine)
  const [importing, setImporting] = useState('')
  const useCases = useAsync<OptimiseUseCase[]>(() => api.optimiseUseCases(), [])
  const models = useAsync<Model[]>(async () => {
    const [priced, catalogue] = await Promise.all([api.models(), api.searchModels({ limit: 2000 }).catch(() => [] as CatalogModel[])])
    const seen = new Set(priced.map((m) => m.canonical))
    return [...priced.map((m) => ({ canonical: m.canonical, family: m.family, label: m.label })),
      ...catalogue.filter((m) => !seen.has(m.canonical)).map((m) => ({ canonical: m.canonical, family: m.family, label: `${m.canonical.split('/').pop()} · self-host only` }))]
  }, [])
  const debounced = useDebounced(doc, 450)
  const out = useAsync<Optimised | null>(() => api.optimise(debounced, applied), [debounced, applied])
  const o = out.data
  // the first answer sets the applied set to the levers' defaults; a changed document keeps the switches
  useEffect(() => { if (o && applied === null) setApplied(o.applied) }, [o, applied])
  useEffect(() => { setApplied(null) }, [debounced.workloads.length]) // a workload added or removed: back to the defaults

  const regionBody = doc.regions ? (doc.regions as Record<string, string>) : regionsFor(regions, vendors)
  const docPeriod: Period = { months: doc.months, growth: doc.growth_pct, end: doc.end_output_tokens_per_day ?? null }
  const change = (next: SimulationDoc) => setDoc(next)
  const setWorkload = (i: number, w: SimulationWorkload) => change({ ...doc, workloads: doc.workloads.map((x, j) => (j === i ? w : x)) })
  const addWorkload = () => change({ ...doc, workloads: [...doc.workloads, { ...fresh(regions, period).workloads[0], label: `Workload ${doc.workloads.length + 1}`, use_case: 'chat' }] })
  const removeWorkload = (i: number) => change({ ...doc, workloads: doc.workloads.filter((_, j) => j !== i) })
  const importKept = async (id: string) => {
    if (!id) return
    setImporting('…')
    try {
      const r = await api.getSimulation(id)
      change({ ...r.doc, workloads: r.doc.workloads.map((w) => ({ ...w, use_case: w.use_case ?? 'chat' })) })
      setApplied(null); setImporting('')
    } catch (e) { setImporting(e instanceof Error ? e.message : String(e)) }
  }
  const toggle = (id: string, on: boolean) => {
    const base = applied ?? o?.applied ?? []
    setApplied(on ? [...base.filter((x) => x !== id), id] : base.filter((x) => x !== id))
  }
  const isOn = (lv: OptimiseLever) => Boolean(lv.priced && (applied ?? o?.applied ?? []).includes(lv.id))
  const n = doc.months
  const ucOf = (id?: string | null) => useCases.data?.find((u) => u.id === id)
  const pdf = (
    <ExportPdf placeholder={doc.client ? `${doc.client} — LLM spend review` : 'LLM spend review'} disabled={o ? undefined : 'The ladder is being priced'}
      summary="The PDF has today against the optimised plan, each applied change with what it saves and assumes, the workloads one by one, and the changes priced but left aside, with the price dates."
      build={(fields) => api.optimiseReport({ doc, applied: applied ?? o?.applied ?? null, ...fields })} />
  )

  return (
    <>
      {embedded ? null : (
      <section className="panel setup simsetup">
        <h2 className="row simtitle">How much less could this client pay for the same work?
          <Help text="Describe what the client runs today. Every lever below is priced from the vendors' list prices at the client's own volumes; nothing is projected and no vendor is favoured. Where a change needs the client's judgement, the row says so and stays off until you apply it." />
          <span className="spacer" />
          {mine.length ? (
            <select className="optimport" value="" onChange={(e) => void importKept(e.target.value)} aria-label="Start from a kept simulation">
              <option value="">Start from a kept simulation…</option>
              {mine.map((m) => <option key={m.id} value={m.id}>{m.name || m.client || m.id}</option>)}
            </select>
          ) : null}
        </h2>
        {importing && importing !== '…' ? <ErrorNote error={importing} /> : null}

        <h3 className="step">1 · The client and the period<Help text="Who it is for (printed on the PDF), where each vendor is priced, and the period with its growth. Every month is priced at today's list prices." /></h3>
        <input type="text" className="simclient" value={doc.client} placeholder="Client (optional)" maxLength={120} onChange={(e) => change({ ...doc, client: e.target.value })} />
        <RegionPicker regions={doc.regions ? { ...regions, ...(doc.regions as RegionChoices) } : regions} compact bare
          onChange={(r) => { setRegions(r); change({ ...doc, regions: regionsFor(r, vendors) }) }} />
        <TimeStep value={docPeriod} onChange={(next) => { setPeriod(next); change({ ...doc, months: next.months, growth_pct: next.end ? 0 : next.growth, end_output_tokens_per_day: next.end ?? null }) }} />

        <h3 className="step">2 · What runs today<Help text="One card per workload as the client runs it now: the model, the volume, the cloud and mode with the tick, and what the workload is for. The use case decides which levers may be offered: batch and flex only where nobody waits, caching only where a prompt repeats, a smaller model only where the client can test one." /></h3>
        <div className="simworkloads">
          {doc.workloads.map((w, i) => (
            <WorkloadCard key={i} i={i} w={w} doc={doc} models={models.data ?? null} regions={regionBody} vendors={vendors} readOnly={false}
              onChange={(next) => setWorkload(i, next)} onRemove={doc.workloads.length > 1 ? () => removeWorkload(i) : undefined}
              hint="each chip is this workload's month-1 cost on that cloud and mode; the one with the tick is where the client runs it today. The ladder prices every other way of running it."
              extra={(
                <>
                  <div className="field"><label>Use case</label>
                    <select value={w.use_case ?? 'chat'} onChange={(e) => setWorkload(i, { ...w, use_case: e.target.value })} title={ucOf(w.use_case ?? 'chat')?.note}>
                      {(useCases.data ?? [{ id: 'chat', label: 'Chat assistant' } as OptimiseUseCase]).map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
                    </select>
                  </div>
                  <div className="field"><label>Tokens per request<Help text="How long a request usually is, input included. Left blank, the use case's usual size; given, and well above it, trimming the request is offered as a lever." /></label>
                    <input type="number" min={100} step={1000} value={w.context_tokens ?? ''} placeholder={String(ucOf(w.use_case ?? 'chat')?.context_tokens ?? '')}
                      onChange={(e) => setWorkload(i, { ...w, context_tokens: e.target.value ? Math.max(100, Number(e.target.value) || 100) : null })} />
                  </div>
                </>
              )} />
          ))}
        </div>
        {doc.workloads.length < 8 ? <button type="button" className="pill" onClick={addWorkload}>+ Add a workload</button> : null}
      </section>
      )}

      <section className="panel datapanel">
        <h2 className="row">The savings ladder<Help text="Largest first. Each row is that one change on today's setup, priced with the same tokens, ratio and period. Switch a row off if the client will not make that change; the tiles and the chart follow. Rows on the same workload are priced together in the plan, never added twice." />
          {o ? <span className="tag">{(applied ?? o.applied).length} of {o.ladder.filter((l) => l.priced).length} applied</span> : null}
          <span className="spacer" />
          <span className="muted small">{out.loading ? 'pricing…' : `today's list prices · ${n} months`}</span>
          {o ? <Useful page="optimise" /> : null}
          {pdf}
        </h2>
        <ErrorNote error={out.error} />
        {o ? (
          <>
            <div className="kpis">
              <div className="kpi"><span className="k">Today, {n} months</span><b>{usd(o.totals.today_usd, 0)}</b><span className="f">{o.workloads.length} workload{o.workloads.length === 1 ? '' : 's'} at list prices{o.growth_pct ? ` · +${o.growth_pct}% a month` : ''}</span></div>
              <div className="kpi good"><span className="k">Optimised</span><b>{usd(o.totals.optimised_usd, 0)}</b><span className="f">{(applied ?? o.applied).length} change{(applied ?? o.applied).length === 1 ? '' : 's'} · the same work</span></div>
              <div className="kpi good"><span className="k">Saving</span><b>−{usd(o.totals.saving_usd, 0)}</b><span className="f">{o.totals.saving_pct}% less · {usd(o.totals.saving_usd / n, 0)} a month</span></div>
              <div className="kpi"><span className="k">Not priced</span><b>{o.ladder.filter((l) => !l.priced && l.saves_usd == null).length || 'none'}</b><span className="f">{o.ladder.some((l) => l.lever === 'spot') ? 'spot prices are not collected yet' : 'every lever that applies is priced'}</span></div>
            </div>
            {o.workloads.filter((w) => !w.today.available).map((w) => (
              <p key={w.index} className="note small"><b>{w.label}</b> is not priced: {w.today.reason}</p>
            ))}
            <div className="table-wrap simtable">
              <table className="ladder">
                <thead><tr><th>Apply</th><th>Change</th><th>Workload</th><th>Lever</th><th className="num">Saves, {n} mo</th></tr></thead>
                <tbody>
                  {o.ladder.map((lv) => (
                    <tr key={lv.id} className={lv.priced && !isOn(lv) ? 'off' : ''}>
                      <td>{lv.priced ? <Switch on={isOn(lv)} label={`Apply: ${lv.title}`} onChange={(v) => toggle(lv.id, v)} /> : <Switch on={false} disabled label={lv.title} onChange={() => {}} />}</td>
                      <td className="lever"><b>{lv.title}</b><span className="muted small">{lv.why}{lv.assumption ? <> <i>Assumes: {lv.assumption}</i></> : null}</span>
                        {lv.needs_decision && lv.priced ? <span className="tag plain">the client's call</span> : null}</td>
                      <td>{o.workloads[lv.workload]?.label}</td>
                      <td><span className="tag plain">{LEVER_LABEL[lv.lever] ?? lv.lever}</span></td>
                      <td className={`num save${lv.saves_usd ? '' : ' zero'}`}>{lv.saves_usd == null ? 'not priced' : lv.saves_usd ? `−${usd(lv.saves_usd, 0)}` : 'nothing to gain'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {n > 1 ? <Chart months={n} today={o.totals.today_monthly_usd} optimised={o.totals.optimised_monthly_usd} /> : null}
            <div className="table-wrap simtable">
              <table>
                <thead><tr><th>Workload</th><th>Use case</th><th>Today</th><th>Optimised</th><th className="num">Over {n} months</th></tr></thead>
                <tbody>
                  {o.workloads.map((w) => (
                    <tr key={w.index}>
                      <td><b>{w.label}</b><div className="muted small">{compact(doc.workloads[w.index]?.output_tokens_per_day ?? 0)} output tokens a day</div></td>
                      <td>{w.use_case_label}</td>
                      <td>{w.today.available ? w.today.label : <span className="muted">{w.today.reason}</span>}</td>
                      <td>{w.optimised.available ? w.optimised.label : '—'}</td>
                      <td className="num">{w.today.available ? <>{usd(w.today.total_usd ?? 0, 0)} → <b>{usd(w.optimised.total_usd ?? 0, 0)}</b></> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="assumptions">{o.basis.charAt(0).toUpperCase() + o.basis.slice(1)}. Model changes are priced, never judged: whether a smaller model is good enough is the client's test to run. Cached-input and batch prices are each vendor's published rates; the cache hit rate, the repeated prefix and the routing split are assumptions named on the row.</p>
          </>
        ) : out.loading ? <p className="muted small">pricing…</p> : null}
      </section>
    </>
  )
}
